<template>
  <div>
    <kt-datatable
      :model="model"
      :data="rows"
      :filter="datatableFilter"
      :update-function="handleUpdate"
      :hide-search="false"
      storage-key="attachment-list"
    >
      <template #filters>
        <div class="d-flex flex-wrap gap-2 align-items-center">
          <kt-autoselect
            label="Type"
            :options="typeOptions"
            display-by="name"
            value-by="id"
            :multiple="true"
            v-model="currentFilter.type"
            search-placeholder="Search types..."
            @change="applyType"
          />
          <button v-if="currentFilter.type.length || currentFilter.searchTerm" class="btn btn-sm btn-outline-secondary" @click="clearFilters">Clear All</button>
        </div>
      </template>

      <template #after-search>
        <button class="btn btn-sm btn-outline-secondary" @click="loadData">Refresh <i class="fa fa-sync"></i></button>
      </template>

      <template #view_string="scope">
        <a v-if="scope.column.name === 'Name'" href="#" @click.prevent.stop="viewFile(scope.row)">{{ scope.row.Name }}</a>
        <router-link v-else-if="scope.column.name === 'LinkedRecord' && scope.row.LinkedRecordRoute" :to="scope.row.LinkedRecordRoute" @click.stop>
          {{ scope.row.LinkedRecord }}
        </router-link>
        <span v-else-if="scope.column.name === 'Type'" class="badge bg-secondary">{{ formatType(scope.value) }}</span>
        <span v-else>{{ scope.value || '-' }}</span>
      </template>

      <template #view_actions="scope">
        <div class="btn-group btn-group-sm">
          <button class="btn btn-outline-primary" title="View" :disabled="loadingId === scope.row.Id" @click.stop="viewFile(scope.row)">
            <i class="fa fa-eye"></i>
          </button>
          <button class="btn btn-outline-success" title="Download" :disabled="loadingId === scope.row.Id" @click.stop="downloadFile(scope.row)">
            <i class="fa fa-download"></i>
          </button>
          <button class="btn btn-outline-danger" title="Delete" :disabled="loadingId === scope.row.Id" @click.stop="deleteFile(scope.row)">
            <i class="fa fa-trash"></i>
          </button>
        </div>
      </template>
    </kt-datatable>
    <div v-if="requestState.IsError" class="alert alert-danger mt-3">{{ requestState.Message || 'Unable to load files.' }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter, RouterLink } from 'vue-router';
import KtDatatable from '@/components/UI/KtDatatable.vue';
import KtAutoselect from '@/components/UI/KtAutoselect.vue';
import type { KtDatatableModelProp } from '@/components/UI/KtDataTable';
import ApiService from '@/services/ApiService';
import AttachmentService from '@/services/AttachmentService';
import Alert from '@/services/Alert';
import { useApiRequest, useDatatableSettings } from '@/composables';
import type { AttachmentFilter, AttachmentListItem } from '@/models/AttachmentModel';

interface FilesFilter {
  type: string[];
  searchTerm: string;
  page: number;
  size: number;
  sort: Array<Record<string, 'ASC' | 'DESC'>>;
}

const route = useRoute();
const router = useRouter();
const rows = ref<AttachmentListItem[]>([]);
const total = ref(0);
const loadingId = ref<string | null>(null);
const initializing = ref(true);
const { state: requestState, execute } = useApiRequest<AttachmentListItem[]>('files:list');
const { loadSettings, saveFilters } = useDatatableSettings('AttachmentDatatable');
const currentFilter = ref<FilesFilter>({ type: [], searchTerm: '', page: 0, size: 25, sort: [{ Created: 'DESC' }] });
const attachmentTypes = ['request', 'part', 'expense', 'mileage', 'task', 'invoice', 'workorder', 'notification', 'contact', 'technician'];
const typeOptions = attachmentTypes.map(id => ({ id, name: id.charAt(0).toUpperCase() + id.slice(1) }));

const model: KtDatatableModelProp[] = [
  { name: 'Name', label: 'Name', type: 'string', display: true, sortable: true },
  { name: 'Type', label: 'Type', type: 'string', display: true, sortable: true },
  { name: 'LinkedRecord', label: 'Linked Record', type: 'string', display: true, sortable: true },
  { name: 'Created', label: 'Created', type: 'date', display: true, sortable: true },
  { name: 'Modified', label: 'Modified', type: 'date', display: true, sortable: true },
  { name: 'Actions', label: '', type: 'actions', display: true, width: 125, locked: true }
];

const datatableFilter = computed(() => ({
  page: currentFilter.value.page,
  size: currentFilter.value.size,
  total: total.value,
  search: currentFilter.value.searchTerm,
  sort: currentFilter.value.sort
}));

const buildOrderBy = () => currentFilter.value.sort.map(item => {
  const [name, direction] = Object.entries(item)[0];
  return direction === 'DESC' ? `-${name}` : name;
}).join(',');

const apiFilter = (): AttachmentFilter => ({
  Type: currentFilter.value.type,
  SearchTerm: currentFilter.value.searchTerm,
  Page: currentFilter.value.page,
  Size: currentFilter.value.size,
  OrderBy: buildOrderBy()
});

const syncUrl = () => {
  const query: Record<string, string> = {};
  if (currentFilter.value.type.length) query.type = currentFilter.value.type.join(',');
  if (currentFilter.value.searchTerm) query.search = currentFilter.value.searchTerm;
  if (currentFilter.value.page) query.page = String(currentFilter.value.page);
  if (currentFilter.value.size !== 25) query.size = String(currentFilter.value.size);
  router.replace({ path: '/files', query });
};

const loadData = async () => {
  await execute(() => ApiService.loadAttachments(apiFilter()));
  rows.value = requestState.value.Data || [];
  total.value = requestState.value.Meta?.Total || 0;
};

const handleUpdate = async (update: any) => {
  if (initializing.value) return;
  if (update.page !== undefined) currentFilter.value.page = update.page;
  if (update.size !== undefined) currentFilter.value.size = update.size;
  if (update.search !== undefined) currentFilter.value.searchTerm = update.search;
  if (update.sort !== undefined) currentFilter.value.sort = update.sort || [];
  await saveFilters(currentFilter.value);
  syncUrl();
  await loadData();
};

const applyType = async () => {
  currentFilter.value.page = 0;
  await saveFilters(currentFilter.value);
  syncUrl();
  await loadData();
};

const clearFilters = async () => {
  currentFilter.value.type = [];
  currentFilter.value.searchTerm = '';
  currentFilter.value.page = 0;
  await saveFilters(currentFilter.value);
  syncUrl();
  await loadData();
};

const formatType = (type: string) => type ? type.charAt(0).toUpperCase() + type.slice(1) : 'File';

const viewFile = async (file: AttachmentListItem) => {
  loadingId.value = file.Id;
  try {
    if (AttachmentService.isWebViewable(file.Name)) await AttachmentService.viewFileInTab(file.Id, file.ContentType);
    else await AttachmentService.downloadFile(file.Id, file.Name);
  } finally {
    loadingId.value = null;
  }
};

const downloadFile = async (file: AttachmentListItem) => {
  loadingId.value = file.Id;
  try {
    await AttachmentService.downloadFile(file.Id, file.Name);
  } finally {
    loadingId.value = null;
  }
};

const deleteFile = (file: AttachmentListItem) => {
  Alert.confirm(`Are you sure you want to delete "${file.Name}"?`, async () => {
    loadingId.value = file.Id;
    try {
      await ApiService.deleteAttachment(file.Id);
      await loadData();
    } finally {
      loadingId.value = null;
    }
  });
};

onMounted(async () => {
  const hasQuery = Object.keys(route.query).length > 0;
  if (hasQuery) {
    currentFilter.value.type = String(route.query.type || '').split(',').filter(Boolean);
    currentFilter.value.searchTerm = String(route.query.search || '');
    currentFilter.value.page = Number(route.query.page || 0);
    currentFilter.value.size = Number(route.query.size || 25);
  } else {
    const settings = await loadSettings();
    if (settings?.filters) currentFilter.value = { ...currentFilter.value, ...settings.filters };
    syncUrl();
  }
  initializing.value = false;
  await loadData();
});
</script>